<template>
  <div>
    <div class="seo-score-group">
      <div class="seo-score-group__chart">
        <SeoScoreProgressBar
          :green="stats.GREEN / seoScoreMetricNames.length * 100"
          :yellow="(stats.GREEN + stats.YELLOW) / seoScoreMetricNames.length * 100"
        >
          <div class="d-flex flex-column align-center">
            <div class="text-h4">
              {{ seoScore }}%
            </div>
            <div class="mt-1 text-caption">
              {{ $t('seoScore.score') }}
            </div>
          </div>
        </SeoScoreProgressBar>
        <div class="seo-score-legend">
          <button
            v-for="item in legendItems"
            :key="item.status"
            type="button"
            class="seo-score-count"
            :class="[
              'seo-score-count--' + item.status.toLowerCase(),
              { 'seo-score-count--active': statusFilter === item.status }
            ]"
            :aria-pressed="statusFilter === item.status ? 'true' : 'false'"
            :aria-label="item.label + ': ' + item.count"
            @click.stop="toggleStatusFilter(item.status)"
          >
            <strong>{{ item.count }}</strong>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </div>
      <v-btn
        v-if="!demo && $store.getters.article.id"
        block
        depressed
        color="primary"
        class="multi-line-button seo-score-group__cta"
        @click.stop="openDetailedScore"
      >
        <v-spacer />
        <v-icon class="mr-2">insights</v-icon>
        {{ $t('seoScore.detail.openPage') }}
        <v-spacer />
      </v-btn>
      <v-expand-transition>
        <v-list
          v-show="statusFilter"
          class="seo-score-list"
        >
          <template v-if="visibleMetricNames.length">
            <div
              v-for="metricName of visibleMetricNames"
              :key="metricName"
              class="lucy-factor-item"
            >
              <v-tooltip
                right
                class="lucy-factor-tip"
              >
                <template #activator="{ on }">
                  <button
                    type="button"
                    class="lucy-factor-hit"
                    v-on="on"
                    @click="getClickAction(metricName)?.()"
                  >
                    <SeoScoreIcon :status="demo ? 'GREEN' : getStatus(metricName)" />
                    <span class="lucy-factor-label text-caption">{{ $t('seoScore.' + metricName + '.label') }}</span>
                  </button>
                </template>
                <div
                  v-if="$te('seoScore.' + metricName + '.description')"
                  class="font-weight-bold mb-2"
                >
                  {{ $t('seoScore.' + metricName + '.description', getTrParams(metricName)) }}
                  <template v-if="metricName === 'keywordInSlug' && isAnalyze">
                    {{ $t('article.urlSuggestion.redirectNote') }}
                  </template>
                </div>
                <div>
                  <SeoScoreIcon :status="demo ? 'GREEN' : getStatus(metricName)" />
                  {{
                    $t('seoScore.' + metricName + '.' + (demo ? 'green' : getStatus(metricName).toLowerCase()), getTrParams(metricName))
                  }}
                </div>
              </v-tooltip>
              <span
                v-if="!demo"
                class="lucy-factor-spark"
                @mouseenter.stop
                @mouseover.stop
                @mousemove.stop
              >
                <LucySpark :metric="metricName" />
              </span>
            </div>
          </template>
          <v-list-item
            v-else
            class="v-list-item--superdense"
          >
            <span class="ml-2 text-caption text--secondary">{{ $t('seoScore.emptyGroup') }}</span>
          </v-list-item>
        </v-list>
      </v-expand-transition>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from "vue";
    import {
        seoScoreContext,
        SeoScoreContext,
        SeoScoreMetricName,
        seoScoreMetricNames,
        SeoScoreStatus
    } from "@/model/SeoScore";
    import SeoScoreIcon from "@/components/seoscore/SeoScoreIcon.vue";
    import SeoScoreProgressBar from "@/components/SeoScoreProgressBar.vue";
    import SeoScoreMetricsMixin from "@/mixins/SeoScoreMetricsMixin";
    import LucySpark from "@/components/lucy/LucySpark.vue";

    type SeoScoreVm = Vue & {
        getStatus(name: SeoScoreMetricName): SeoScoreStatus;
        getTrParams(name: SeoScoreMetricName): Array<unknown>;
        stats: Record<SeoScoreStatus, number>;
        getClickAction(name: SeoScoreMetricName): (() => void) | undefined;
    };

    function asSeoVm(vm: Vue): SeoScoreVm {
        return vm as SeoScoreVm;
    }

    export default Vue.extend({
        name: "SeoScore",
        components: {SeoScoreProgressBar, SeoScoreIcon, LucySpark},
        mixins: [SeoScoreMetricsMixin],
        props: {
            context: {
                type: Object as PropType<SeoScoreContext>,
                default: () => seoScoreContext
            },
            demo: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            statusFilter: null as SeoScoreStatus | null,
            seoScoreMetricNames: seoScoreMetricNames,
        }),
        computed: {
            loadingCompleted: function (): boolean {
                return this.$store.getters.articleAiLoading.loadingCompleted;
            },
            isAnalyze: function (): boolean {
                return !!this.$store.getters.article.isAnalyze;
            },
            legendItems(): Array<{ status: SeoScoreStatus; count: number; label: string }> {
                const vm = asSeoVm(this);
                return [
                    {status: 'RED', count: vm.stats.RED, label: String(this.$t('seoScore.legendRed'))},
                    {status: 'YELLOW', count: vm.stats.YELLOW, label: String(this.$t('seoScore.legendYellow'))},
                    {status: 'GREEN', count: vm.stats.GREEN, label: String(this.$t('seoScore.legendGreen'))},
                ];
            },
            visibleMetricNames(): SeoScoreMetricName[] {
                if (!this.statusFilter) {
                    return [];
                }
                if (this.demo) {
                    return this.statusFilter === 'GREEN' ? [...seoScoreMetricNames] : [];
                }
                const vm = asSeoVm(this);
                return seoScoreMetricNames.filter((name) => vm.getStatus(name) === this.statusFilter);
            }
        },
        watch: {
            loadingCompleted: {
                handler: function (val: boolean) {
                    if (!val || this.$store.getters.sharePreview) {
                        if (val && this.$store.getters.sharePreview) {
                            this.$store.commit('setArticleSeoScoreChecked', true);
                        }
                        return;
                    }
                    // Score history is filled by an explicit save. Opening must not
                    // write seoScore or show the saved toast.
                    this.$store.commit('setArticleSeoScoreChecked', true);
                },
                immediate: true,
            }
        },
        created() {
            if (this.$store.getters.sharePreview) {
                this.$store.commit('setArticleSeoScoreChecked', true);
                return;
            }
            if (!this.$store.getters.articleAiLoading.loadingCompleted) {
                this.$store.commit('setArticleSeoScoreChecked', false);
            }
        },
        methods: {
            toggleStatusFilter(status: SeoScoreStatus): void {
                this.statusFilter = this.statusFilter === status ? null : status;
            },
            openDetailedScore(): void {
                const articleId = this.$store.getters.article.id;
                const shareToken = this.$store.getters.sharePreviewToken;
                const query: Record<string, string> = {};
                if (articleId) {
                    query.articleId = articleId;
                }
                if (shareToken) {
                    query.share = shareToken;
                }
                this.$router.push({
                    name: 'SeoScoreDetail',
                    query
                });
            }
        }
    });
</script>

<style scoped lang="sass">
.seo-score-group
    width: 95%
    margin: 12px auto 0
    padding: 14px 10px 10px
    border-radius: 16px
    background: rgba(8, 135, 220, 0.1)
    box-shadow: inset 0 0 0 1px rgba(8, 135, 220, 0.22)

.theme--dark .seo-score-group
    background: rgba(8, 135, 220, 0.18)
    box-shadow: inset 0 0 0 1px rgba(8, 135, 220, 0.38)

.seo-score-group__chart
    position: relative

.seo-score-legend
    display: grid
    grid-template-columns: 1fr 1fr 1fr
    gap: 6px
    margin: 10px 2px 8px

.seo-score-count
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    min-height: 44px
    border: 0
    border-radius: 10px
    cursor: pointer
    padding: 6px 4px
    line-height: 1.15
    strong
        font-size: 16px
        font-weight: 800
    span
        font-size: 9px
        font-weight: 700
        letter-spacing: 0.02em
        text-transform: uppercase
        margin-top: 2px

.seo-score-count--red
    background: var(--v-error-base)
    color: #fff

.seo-score-count--yellow
    background: var(--v-warning-base)
    color: #fff

.seo-score-count--green
    background: var(--v-accent-base)
    color: #fff

.seo-score-count--active
    box-shadow: 0 0 0 2px #0887dc, 0 0 0 4px rgba(8, 135, 220, 0.25)

.seo-score-group__cta
    width: 100% !important
    display: flex !important
    font-weight: 700
    letter-spacing: 0.04em
    min-height: 3em

.seo-score-list
    margin-top: 10px
    background: transparent !important
    padding: 0
    .v-list-item
        min-height: 32px
        padding: 0 8px
        .v-list-item__action
            margin: 4px 0
            min-width: 32px
            .v-icon
                font-size: 16px
        .v-list-item__content
            padding: 4px 0
            .v-list-item__title
                font-size: 13px
                line-height: 1.3

.lucy-factor-item
    display: flex
    flex-wrap: nowrap
    align-items: center
    width: 100%
    min-height: 28px
    padding: 3px 8px
    box-sizing: border-box

.lucy-factor-hit
    display: inline-flex
    align-items: center
    max-width: 100%
    margin: 0
    padding: 0
    border: 0
    background: transparent
    color: inherit
    font: inherit
    text-align: left
    cursor: pointer
    line-height: 1.35

.lucy-factor-label
    margin-left: 8px
    line-height: 1.35
    white-space: normal

.lucy-factor-spark
    display: inline-flex
    align-items: center
    flex: 0 0 auto
    margin-left: 8px
    position: relative
    z-index: 2
</style>

<style lang="sass">
.seo-score-list .lucy-factor-tip.v-tooltip
    display: inline-flex !important
    flex: 0 1 auto
    width: auto !important
    min-width: 0
    max-width: calc(100% - 36px)
    vertical-align: middle
</style>
